/**
 * SAD-954 — one row per notification kind: an in-app switch, an email mode
 * (Instant / Daily digest / Off), what resetting falls back to, and a reset.
 * Shared by Settings → Notifications (a person's own choices, on top of the
 * company defaults) and the company defaults section (on top of Specra's
 * built-in defaults).
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraDataTable, SpecraSelect, SpecraSwitch } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
export const EMAIL_MODE_LABELS = {
    INSTANT: 'Instant',
    DAILY_DIGEST: 'Daily digest',
    OFF: 'Off'
};
const EMAIL_MODES = [
    'INSTANT',
    'DAILY_DIGEST',
    'OFF'
];
function defaultText(k) {
    const parts = [
        `in-app ${k.inApp?.defaultMode === 'OFF' ? 'off' : 'on'}`
    ];
    if (k.email?.available) {
        parts.push(`email ${(EMAIL_MODE_LABELS[k.email.defaultMode] ?? k.email.defaultMode).toLowerCase()}`);
    }
    return parts.join(', ');
}
// specra-allow local-composite: thin wrapper that configures SpecraDataTable columns
export function NotificationPreferencesTable({ kinds, ownSource, onUpdate, pending }) {
    const anyChosen = kinds.some((k)=>k.inApp?.source === ownSource || k.email?.source === ownSource);
    const isChosen = (k)=>k.inApp?.source === ownSource || k.email?.source === ownSource;
    const columns = [
        {
            id: 'notification',
            header: 'Notification',
            cell: (k)=>/*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "font-medium",
                            children: k.label
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "text-xs text-muted-foreground",
                            children: k.description
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-1 text-xs text-muted-foreground",
                            "data-testid": Testids.notificationPreferences.defaultLabel,
                            children: [
                                "Default: ",
                                defaultText(k)
                            ]
                        })
                    ]
                })
        },
        {
            id: 'inApp',
            header: 'In-app',
            width: '6rem',
            cell: (k)=>/*#__PURE__*/ _jsx(SpecraSwitch, {
                    "aria-label": `${k.label} in-app`,
                    checked: k.inApp?.mode !== 'OFF',
                    disabled: pending,
                    onCheckedChange: (on)=>onUpdate({
                            changes: [
                                {
                                    kind: k.kind,
                                    channel: 'IN_APP',
                                    mode: on ? 'ON' : 'OFF'
                                }
                            ]
                        }),
                    "data-testid": Testids.notificationPreferences.inAppSwitch
                })
        },
        {
            id: 'email',
            header: 'Email',
            width: '11rem',
            cell: (k)=>k.email?.available ? /*#__PURE__*/ _jsx(SpecraSelect, {
                    size: "sm",
                    "aria-label": `${k.label} email`,
                    value: k.email.mode,
                    disabled: pending,
                    onChange: (e)=>onUpdate({
                            changes: [
                                {
                                    kind: k.kind,
                                    channel: 'EMAIL',
                                    mode: e.target.value
                                }
                            ]
                        }),
                    "data-testid": Testids.notificationPreferences.emailSelect,
                    children: EMAIL_MODES.map((m)=>/*#__PURE__*/ _jsx("option", {
                            value: m,
                            children: EMAIL_MODE_LABELS[m]
                        }, m))
                }) : /*#__PURE__*/ _jsx("span", {
                    className: "text-xs text-muted-foreground",
                    title: "Specra does not email these",
                    children: "In-app only"
                })
        },
        {
            id: 'reset',
            header: '',
            width: '5rem',
            align: 'right',
            cell: (k)=>isChosen(k) && /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    size: "sm",
                    disabled: pending,
                    onClick: ()=>onUpdate({
                            changes: [
                                {
                                    kind: k.kind,
                                    channel: 'IN_APP',
                                    mode: ''
                                },
                                ...k.email?.available ? [
                                    {
                                        kind: k.kind,
                                        channel: 'EMAIL',
                                        mode: ''
                                    }
                                ] : []
                            ]
                        }),
                    "data-testid": Testids.notificationPreferences.rowReset,
                    children: "Reset"
                })
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex items-center justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    size: "sm",
                    disabled: pending || !anyChosen,
                    onClick: ()=>onUpdate({
                            resetAll: true
                        }),
                    "data-testid": Testids.notificationPreferences.resetAll,
                    children: "Reset all to defaults"
                })
            }),
            /*#__PURE__*/ _jsx(SpecraDataTable, {
                testId: Testids.notificationPreferences.table,
                columns: columns,
                rows: kinds,
                getRowId: (k)=>k.kind,
                rowTestId: Testids.notificationPreferences.row,
                getRowProps: (k)=>({
                        'data-kind': k.kind
                    })
            })
        ]
    });
}
